import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type AdminActivity } from '../lib/api';
import { fmt } from './Activities';

interface Option { id: string; label: string }
interface Question { id: string; prompt: string; options: Option[]; correctOptionIds: string[]; explanation: string; points: string }
type Kind = 'poll' | 'quiz';
interface Draft {
  type: Kind; slug: string; title: string; opensAt: string; closesAt: string;
  // what the card shows (every type)
  description: string; imageUrl: string; buttonLabel: string; kicker: string;
  // poll
  question: string; options: Option[]; afterLine: string;
  // quiz
  intro: string; questions: Question[]; revealAnswers: boolean;
}

const newOption = (): Option => ({ id: '', label: '' });
const newQuestion = (): Question => ({ id: '', prompt: '', options: [newOption(), newOption()], correctOptionIds: [], explanation: '', points: '10' });
const empty: Draft = {
  type: 'poll', slug: '', title: '', opensAt: '', closesAt: '', description: '', imageUrl: '', buttonLabel: '', kicker: '',
  question: '', options: [newOption(), newOption()], afterLine: '', intro: '', questions: [newQuestion()], revealAnswers: true,
};
const orEmpty = (v: unknown) => (typeof v === 'string' ? v : '');
const toLocal = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');
const toIso = (local: string) => (local ? new Date(local).toISOString() : null);
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 79);
const optId = (o: Option) => o.id.trim() || slugify(o.label);

/** Activity builder: poll or quiz. Versions are immutable; publish moves the pointer. Quiz answer keys stay server-side. */
export function ActivityEditor() {
  const { orgId, activityId } = useParams();
  const nav = useNavigate();
  const [activity, setActivity] = useState<AdminActivity | null>(null);
  const [draft, setDraft] = useState<Draft>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!activityId) return;
    api.activities.get(orgId!, activityId).then((a) => { setActivity(a); setDraft(fromActivity(a)); }, (e: Error) => setError(e.message));
  }, [orgId, activityId]);

  function fromActivity(a: AdminActivity): Draft {
    const latest = a.versions[a.versions.length - 1]!;
    const shared = latest.definition as { description?: string | null; imageUrl?: string | null; buttonLabel?: string | null; kicker?: string | null };
    const base = {
      ...empty, type: a.type as Kind, slug: a.slug, title: a.title, opensAt: toLocal(a.opensAt), closesAt: toLocal(a.closesAt),
      description: orEmpty(shared.description), imageUrl: orEmpty(shared.imageUrl), buttonLabel: orEmpty(shared.buttonLabel), kicker: orEmpty(shared.kicker),
    };
    if (a.type === 'quiz') {
      const def = latest.definition as { intro?: string | null; revealAnswers?: boolean; questions: { id: string; prompt: string; options: Option[]; correctOptionId?: string | null; correctOptionIds?: string[] | null; explanation?: string | null; points?: number | null }[] };
      return {
        ...base, intro: def.intro ?? '', revealAnswers: def.revealAnswers ?? false,
        questions: def.questions.map((q) => ({
          id: q.id, prompt: q.prompt, options: q.options,
          correctOptionIds: q.correctOptionIds?.length ? q.correctOptionIds : q.correctOptionId ? [q.correctOptionId] : [],
          explanation: orEmpty(q.explanation), points: q.points == null ? '' : String(q.points),
        })),
      };
    }
    const def = latest.definition as { question: string; options: Option[]; afterLine?: string | null };
    return { ...base, question: def.question, options: def.options, afterLine: orEmpty(def.afterLine) };
  }

  const presentation = () => ({
    description: draft.description.trim() || null,
    imageUrl: draft.imageUrl.trim() || null,
    buttonLabel: draft.buttonLabel.trim() || null,
    kicker: draft.kicker.trim() || null,
  });
  const definition = () =>
    draft.type === 'quiz'
      ? {
          ...presentation(),
          intro: draft.intro.trim() || null,
          revealAnswers: draft.revealAnswers,
          questions: draft.questions.map((q, i) => ({
            id: q.id.trim() || `q${i + 1}`,
            prompt: q.prompt.trim(),
            options: q.options.map((o) => ({ id: optId(o), label: o.label.trim() })),
            correctOptionIds: q.correctOptionIds,
            explanation: q.explanation.trim() || null,
            points: q.points.trim() === '' ? null : Number(q.points),
          })),
        }
      : { ...presentation(), question: draft.question.trim(), options: draft.options.map((o) => ({ id: optId(o), label: o.label.trim() })), afterLine: draft.afterLine.trim() || null };

  const [uploading, setUploading] = useState(false);
  async function pickPicture(file: File | undefined) {
    if (!file) return;
    setUploading(true); setError('');
    try { setDraft({ ...draft, imageUrl: (await api.media.upload(orgId!, file)).url }); } catch (err) { setError((err as Error).message); } finally { setUploading(false); }
  }

  async function run(label: string, fn: () => Promise<AdminActivity>) {
    setBusy(true); setError(''); setNote('');
    try {
      const a = await fn();
      setActivity(a); setDraft(fromActivity(a)); setNote(label);
      if (!activityId) nav(`/orgs/${orgId}/activities/${a.id}`, { replace: true });
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!activity) {
      void run('Draft created.', () => api.activities.create(orgId!, {
        type: draft.type, slug: draft.slug || slugify(draft.title), title: draft.title.trim(), definition: definition(),
        opensAt: toIso(draft.opensAt), closesAt: toIso(draft.closesAt),
      }));
    } else {
      void run('New version saved (not live until you publish it).', () => api.activities.addVersion(orgId!, activity.id, definition()));
    }
  };

  const live = activity?.versions.find((v) => v.id === activity.currentVersionId);
  const latest = activity?.versions[activity.versions.length - 1];
  const setOption = (i: number, patch: Partial<Option>) => setDraft({ ...draft, options: draft.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const setQuestion = (i: number, patch: Partial<Question>) => setDraft({ ...draft, questions: draft.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });

  return (
    <>
      <p><Link to={`/orgs/${orgId}`}>← Activities</Link></p>
      <div className="row between">
        <h1>{activity ? activity.title : `New ${draft.type}`} {activity && <span className={`pill ${activity.status}`}>{activity.status}</span>}</h1>
        {activity && (
          <div className="row">
            {latest && latest.id !== activity.currentVersionId && <button className="primary" disabled={busy} onClick={() => void run(`Published v${latest.versionNo}.`, () => api.activities.publish(orgId!, activity.id, latest.id))}>Publish v{latest.versionNo}</button>}
            {activity.status === 'closed' && live && <button disabled={busy} onClick={() => void run('Reopened.', () => api.activities.publish(orgId!, activity.id, live.id))}>Reopen</button>}
            {activity.status === 'published' && <button className="danger" disabled={busy} onClick={() => void run('Closed. Fans can no longer answer.', () => api.activities.close(orgId!, activity.id))}>Close</button>}
          </div>
        )}
      </div>
      {activity && live && <p className="muted">Live: v{live.versionNo} · <a href={activity.definitionUrl!} target="_blank" rel="noreferrer">definition on the CDN</a> · window {fmt(activity.opensAt)} → {fmt(activity.closesAt)}</p>}

      <form onSubmit={save} className="card stack">
        {!activity && (
          <>
            <div className="row">
              <div><label>Type</label>
                <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as Kind })}>
                  <option value="poll">Poll (one question, no right answer)</option>
                  <option value="quiz">Quiz (scored on the server)</option>
                </select>
              </div>
              <div style={{ flex: 2 }}><label>Title</label><input required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder={draft.type === 'quiz' ? 'Austin race-week quiz' : 'Driver of the day'} /></div>
              <div style={{ flex: 1 }}><label>Slug</label><input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder={slugify(draft.title) || 'slug'} /></div>
            </div>
            <div className="row">
              <div style={{ flex: 1 }}><label>Opens (optional)</label><input type="datetime-local" value={draft.opensAt} onChange={(e) => setDraft({ ...draft, opensAt: e.target.value })} /></div>
              <div style={{ flex: 1 }}><label>Closes (optional)</label><input type="datetime-local" value={draft.closesAt} onChange={(e) => setDraft({ ...draft, closesAt: e.target.value })} /></div>
            </div>
          </>
        )}

        <div className="row">
          <div style={{ flex: 2 }}><label>Line under the title (what the card says)</label><input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder={draft.type === 'quiz' ? 'Seven questions on the people and places behind the team.' : 'Round 16, October 2 to 4, at Sepang.'} /></div>
          <div style={{ flex: 1 }}><label>Small line over the title</label><input value={draft.kicker} onChange={(e) => setDraft({ ...draft, kicker: e.target.value })} placeholder={draft.type === 'quiz' ? 'Quiz, Singapore Grand Prix' : 'Poll, Singapore Grand Prix'} /></div>
          <div style={{ flex: 1 }}><label>Button</label><input value={draft.buttonLabel} onChange={(e) => setDraft({ ...draft, buttonLabel: e.target.value })} placeholder={draft.type === 'quiz' ? 'Start the quiz' : 'Vote'} /></div>
        </div>
        <div className="row">
          <div style={{ flex: 2 }}><label>Picture (16:9, shown at the top of the card)</label><input value={draft.imageUrl} onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })} placeholder="https://…/picture.jpg" /></div>
          <div><label>Upload</label><input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploading} onChange={(e) => void pickPicture(e.target.files?.[0])} /></div>
          {draft.imageUrl && <img src={draft.imageUrl} alt="" style={{ width: 160, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 4 }} />}
        </div>

        {draft.type === 'poll' ? (
          <>
            <div><label>Question</label><input required value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} placeholder="Who was your driver of the day?" /></div>
            <div>
              <label>Options (2–10)</label>
              <div className="options">
                {draft.options.map((o, i) => (
                  <div className="option" key={i}>
                    <input value={o.id} onChange={(e) => setOption(i, { id: e.target.value })} placeholder={slugify(o.label) || 'id'} aria-label="Option id" />
                    <input required value={o.label} onChange={(e) => setOption(i, { label: e.target.value })} placeholder="Label" aria-label="Option label" />
                    <button type="button" className="link" disabled={draft.options.length <= 2} onClick={() => setDraft({ ...draft, options: draft.options.filter((_, j) => j !== i) })}>Remove</button>
                  </div>
                ))}
              </div>
              {draft.options.length < 10 && <button type="button" className="link" onClick={() => setDraft({ ...draft, options: [...draft.options, newOption()] })}>+ Add option</button>}
            </div>
            <div><label>Line under the results (optional)</label><input value={draft.afterLine} onChange={(e) => setDraft({ ...draft, afterLine: e.target.value })} placeholder="Thanks for voting. See you at the track." /></div>
          </>
        ) : (
          <>
            <div><label>Intro (optional)</label><input value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} placeholder="Three quick ones before lights out." /></div>
            <label className="row" style={{ alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" style={{ width: 'auto' }} checked={draft.revealAnswers} onChange={(e) => setDraft({ ...draft, revealAnswers: e.target.checked })} /> Show the right answer after each question (the key ships with the quiz)</label>
            {draft.questions.map((q, i) => (
              <div className="card stack" key={i} style={{ background: '#fafbfc' }}>
                <div className="row between">
                  <b>Question {i + 1}</b>
                  <button type="button" className="link" disabled={draft.questions.length <= 1} onClick={() => setDraft({ ...draft, questions: draft.questions.filter((_, j) => j !== i) })}>Remove question</button>
                </div>
                <div className="row">
                  <div style={{ flex: 3 }}><label>Prompt</label><input required value={q.prompt} onChange={(e) => setQuestion(i, { prompt: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label>Points</label><input type="number" min={0} max={1000} value={q.points} onChange={(e) => setQuestion(i, { points: e.target.value })} placeholder="10" /></div>
                </div>
                <div>
                  <label>Options · tick the right one (tick several for a pick-several question)</label>
                  <div className="options">
                    {q.options.map((o, k) => (
                      <div className="option" key={k} style={{ gridTemplateColumns: 'auto 1fr 2fr auto' }}>
                        <input type="checkbox" style={{ width: 'auto' }} aria-label="Right answer" checked={optId(o) !== '' && q.correctOptionIds.includes(optId(o))} onChange={(e) => setQuestion(i, { correctOptionIds: e.target.checked ? [...q.correctOptionIds, optId(o)] : q.correctOptionIds.filter((x) => x !== optId(o)) })} />
                        <input value={o.id} onChange={(e) => setQuestion(i, { options: q.options.map((x, m) => (m === k ? { ...x, id: e.target.value } : x)) })} placeholder={slugify(o.label) || 'id'} aria-label="Option id" />
                        <input required value={o.label} onChange={(e) => setQuestion(i, { options: q.options.map((x, m) => (m === k ? { ...x, label: e.target.value } : x)) })} placeholder="Label" aria-label="Option label" />
                        <button type="button" className="link" disabled={q.options.length <= 2} onClick={() => setQuestion(i, { options: q.options.filter((_, m) => m !== k) })}>Remove</button>
                      </div>
                    ))}
                  </div>
                  {q.options.length < 10 && <button type="button" className="link" onClick={() => setQuestion(i, { options: [...q.options, newOption()] })}>+ Add option</button>}
                </div>
                <div><label>Why (shown with the right answer, optional)</label><input value={q.explanation} onChange={(e) => setQuestion(i, { explanation: e.target.value })} placeholder="Kannapolis is our headquarters; Banbury is the UK factory." /></div>
              </div>
            ))}
            {draft.questions.length < 20 && <button type="button" className="link" onClick={() => setDraft({ ...draft, questions: [...draft.questions, newQuestion()] })}>+ Add question</button>}
          </>
        )}

        <div className="row">
          <button className="primary" disabled={busy}>{activity ? 'Save as new version' : 'Create draft'}</button>
          {note && <span className="ok">{note}</span>}
          {error && <span className="err">{error}</span>}
        </div>
      </form>

      {activity && activity.versions.length > 0 && (
        <>
          <h2>Versions</h2>
          <table>
            <thead><tr><th>Version</th><th>Content</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {[...activity.versions].reverse().map((v) => (
                <tr key={v.id}>
                  <td>v{v.versionNo} {v.id === activity.currentVersionId && <span className="pill published">live</span>}</td>
                  <td className="muted">{summarise(activity.type, v.definition)}</td>
                  <td className="muted">{new Date(v.createdAt).toLocaleString()}</td>
                  <td>{v.id !== activity.currentVersionId && <button className="link" disabled={busy} onClick={() => void run(`Published v${v.versionNo}.`, () => api.activities.publish(orgId!, activity.id, v.id))}>Publish</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function summarise(type: string, definition: unknown): string {
  if (type === 'quiz') {
    const d = definition as { questions: Question[] };
    return `${d.questions.length} question${d.questions.length === 1 ? '' : 's'}: ${d.questions.map((q) => q.prompt).join(' · ')}`;
  }
  const d = definition as { question: string; options: Option[] };
  return `${d.question} — ${d.options.map((o) => o.label).join(' · ')}`;
}
